import { useEffect, useMemo, useState } from 'react';

import { faAdd } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { OrgMemberInfo, useAuthInfo } from '@propelauth/react';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { MatterItem } from './components/MatterItem';
import { Permission } from '../../../../../../../backend/src/auth/permissions/permission';
import useGetMatters, { QueryParams } from '@/api/queries/useGetMatters';
import { useGetPersonalMatter } from '@/api/queries/useGetPersonalMatter';
import EmptyBoxIcon from '@/assets/icons/empty-box.svg?react';
import Button from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import EmptyState from '@/components/molecules/EmptyState';
import SearchBox from '@/components/molecules/SearchBox';
import Pagination from '@/components/organisms/Pagination';
import { useHasPermission } from '@/hooks/useHasPermission';
import { SearchedMatter } from '@/types';

const roles = {
  ADMIN: 'Admin',
};

const MattersPanel = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [searchText, setSearchText] = useState('');
  const [totalPages, setTotalPages] = useState(1);
  const [pageQuery, setPageQuery] = useState(parseInt(searchParams.get('page') || '0') || 0);
  const [isNewSearch, setIsNewSearch] = useState(false);

  const queryParams: QueryParams = useMemo(
    () => ({
      search: searchText,
      page: pageQuery,
    }),
    [searchText, pageQuery],
  );

  const { isLoading, isFetching, data } = useGetMatters(queryParams);
  const { data: personalMatterData } = useGetPersonalMatter();

  const { hasPermission } = useHasPermission();

  // Auth stuff
  const authInfo = useAuthInfo();
  const orgs = authInfo.orgHelper?.getOrgs();
  const asignedOrg = orgs?.length && (orgs[0] as OrgMemberInfo);
  const assignedRole = asignedOrg && asignedOrg?.userAssignedRole;
  const isAdmin = assignedRole === roles['ADMIN'];

  useEffect(() => {
    setTotalPages(data?.pageCount || 1);
  }, [data]);

  const handleSearch = (value: string) => {
    setSearchText(value);
    setPageQuery(0);
    setIsNewSearch(true);
  };

  const handleClickMatterItem = (id: string) => {
    navigate(`/app/chronos/explore?matterId=${id}`);
  };

  const handleClickMatchedCase = (id: string) => {
    navigate(`/app/chronos/case-editor/summary?caseId=${id}`);
  };

  const handleClickEditMatter = (id: string) => {
    navigate(`/app/chronos/matter-editor/${id}`);
  };

  const goToNewMatter = () => {
    navigate(`/app/chronos/matter-creator`);
  };

  const handleGoToNextPage = () => {
    navigate(`/app/chronos/matters?page=${pageQuery + 1}`);
    setPageQuery(pageQuery + 1);
  };

  const handleGoToPreviousPage = () => {
    navigate(`/app/chronos/matters?page=${pageQuery - 1}`);
    setPageQuery(pageQuery - 1);
  };

  const goToPage = (value: number) => {
    navigate(`/app/chronos/matters?page=${value}`);
    setPageQuery(value);
  };

  return (
    <div className="w-2/3 bg-white p-6">
      <div className="flex flex-col">
        <h2 className="mb-1 text-xl font-semibold text-gray-900">Matters</h2>

        <p className="mb-2 text-sm text-gray-600">
          Matters are collections of cases. Any user who is a member of a matter can see all the cases within it.
        </p>
      </div>

      {/* Personal Matter Section */}
      {personalMatterData && (
        <div className="mb-2">
          <MatterItem
            testid="personal-matter"
            matter={personalMatterData}
            onEdit={handleClickEditMatter}
            onView={handleClickMatterItem}
            isCreator={true}
            isSuperAdmin={hasPermission(Permission.MatterViewAll)}
            personalMatter={true}
          />
          <div className="mt-2 border-b border-dashed border-gray-200"></div>
        </div>
      )}

      {/* General Matters Section */}
      <div className="mb-4 flex flex-col">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-row items-center gap-6">
            <SearchBox value={searchText} onSearchCall={handleSearch} placeholder="Search shared matters..." />
            <Button
              data-testid="new-matter-button"
              rounded="base"
              type="brand"
              text="New Matter"
              onClick={goToNewMatter}
              icon={<FontAwesomeIcon icon={faAdd} className="mr-2 text-white" />}
            />
          </div>

          {!isLoading && data?.matters?.length ? (
            <Pagination
              canGetNextPage={pageQuery < totalPages - 1}
              canGetPrevPage={pageQuery > 0}
              prevPage={handleGoToPreviousPage}
              nextPage={handleGoToNextPage}
              currentPage={pageQuery}
              noOfPages={totalPages}
              goToPage={goToPage}
              isNewSearch={isNewSearch}
              setIsNewSearch={setIsNewSearch}
            />
          ) : null}
        </div>
      </div>
      <div className="flex w-full flex-col gap-2">
        {isLoading || isFetching ? (
          <div className="flex h-full w-full flex-col gap-4">
            <div className="flex h-full w-full flex-col gap-4">
              {Array(4)
                .fill(null)
                .map((_, index) => (
                  <Skeleton key={`skeleton-${index}`} className="h-20 w-full bg-gray-100" />
                ))}
            </div>
          </div>
        ) : (
          <>
            {!data?.matters?.length ? (
              <div>
                {!personalMatterData && (
                  <EmptyState
                    title="No matters found"
                    subtitle={
                      isAdmin ? 'Create your first matter!' : 'Contact your Admin to be added to an existing matter.'
                    }
                  />
                )}
                {personalMatterData && searchText === '' && (
                  <div className="my-4 flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-gray-50 p-6 text-center">
                    <div className="mb-3 flex items-center justify-center rounded-full bg-gray-100 p-3">
                      <EmptyBoxIcon className="h-10 w-10 text-gray-700" />
                    </div>
                    <p className="mb-1 text-sm font-medium text-gray-700">You don't have any shared matters yet</p>
                    <p className="text-sm text-gray-600">
                      Create a New Matter or ask your colleagues to invite you to one.
                    </p>
                  </div>
                )}
                {personalMatterData && searchText !== '' && (
                  <EmptyState
                    title="No shared matters found"
                    subtitle="Try adjusting your search terms or create a new matter."
                  />
                )}
              </div>
            ) : (
              <div className="flex w-full flex-col gap-3">
                {data?.matters?.map((matter: SearchedMatter, i: number) => {
                  const isCreator = matter.createdById === authInfo.user?.userId;

                  return (
                    <motion.div
                      key={matter.id}
                      className="text-gray-800"
                      initial={{ opacity: 0, y: 40 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: i * 0.1 }}
                    >
                      <MatterItem
                        testid={`matter-${i}`}
                        matter={matter}
                        onEdit={handleClickEditMatter}
                        onView={handleClickMatterItem}
                        isCreator={isCreator}
                        isSuperAdmin={hasPermission(Permission.MatterViewAll)}
                        matchedCases={matter.matchedCases}
                        onViewMatchedCase={handleClickMatchedCase}
                      />
                    </motion.div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MattersPanel;
